import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import {
  ArrowRight,
  CaretDown,
  CaretRight,
  DownloadSimple,
  Info,
  Tray,
} from "@phosphor-icons/react"
import { sumBy } from "lodash"
import { useState } from "react"
import { ManageCollectionDialog } from "~/components/collections/dialogs/ManageCollectionDialog"
import DownloadManagerDialog from "~/components/forecast/dialogs/DownloadManagerDialog"
import {
  groupAndClassifyCollections,
  groupCollectionsAsItems,
  type OverviewItem,
} from "~/components/collections/overview/classifyCollections"
import { CollectionGroupOverviewRow } from "~/components/collections/overview/CollectionGroupOverviewRow"
import { CollectionOverviewRow } from "~/components/collections/overview/CollectionOverviewRow"
import { NoosGroupOverviewRow } from "~/components/collections/overview/NoosGroupOverviewRow"
import { NoosOverviewRow } from "~/components/collections/overview/NoosOverviewRow"
import { Module, OverviewPage } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Card, CardHeader } from "~/components/reusable/Card"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import type { ICollectionDetails } from "~/hooks/useCollections"
import { useGetCollectionsWithDetails } from "~/hooks/useCollections"
import { usePageTracking } from "~/hooks/usePageTracking"
import { api } from "~/utils/api"
import { DateFormatType, formatDate, startOfTodayUTC } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import { Button, IconButton } from "~/components/reusable/Button"
import { Tooltip } from "~/components/reusable/Tooltip"
import { subWeeks } from "date-fns"

export const Route = createFileRoute("/_auth/demand-planning/")({
  component: RouteComponent,
})

function collectionsFromItems(items: OverviewItem[]): ICollectionDetails[] {
  return items.flatMap((item) => (item.type === "group" ? item.collections : [item.collection]))
}

function SectionDivider({
  label,
  count,
  collections,
}: {
  label: string
  count: number
  collections: ICollectionDetails[]
}) {
  const sales = sumBy(collections, "salesQuantity")
  const forecast = sumBy(collections, "forecastedUnits")
  const preOrders = sumBy(collections, "preOrderedUnits")

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-subtle border-b border-base">
      <span className="label-base">{label}</span>
      <span className="paragraph-sm text-fg-subtle">({count})</span>
      {count > 0 && (
        <div className="ml-auto flex items-center gap-3 paragraph-sm text-fg-subtle">
          <span>
            {formatNumber(sales)} {formatName("sale", true)}
          </span>
          <span className="text-fg-muted">·</span>
          <span>{formatNumber(forecast)} forecast</span>
          <span className="text-fg-muted">·</span>
          <span>
            {formatNumber(preOrders)} {formatName("preorder", true)}
          </span>
        </div>
      )}
    </div>
  )
}

function CardEmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <p className="paragraph-sm text-fg-subtle">{children}</p>
    </div>
  )
}

function ViewAllLink({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-1 paragraph-sm text-fg-subtle hover:text-fg-base transition-colors"
    >
      View all <ArrowRight size={14} />
    </Link>
  )
}

function FashionOverviewItem({
  item,
  variant,
  today,
}: {
  item: OverviewItem
  variant: "historic" | "current" | "future"
  today: Date
}) {
  if (item.type === "group") {
    return (
      <CollectionGroupOverviewRow
        groupId={item.groupId}
        groupName={item.groupName}
        collections={item.collections}
        variant={variant}
        today={today}
      />
    )
  }
  return <CollectionOverviewRow collection={item.collection} variant={variant} today={today} />
}

function HistoricSection({
  items,
  collections,
  today,
}: {
  items: OverviewItem[]
  collections: ICollectionDetails[]
  today: Date
}) {
  const [expanded, setExpanded] = useState(false)
  const Caret = expanded ? CaretDown : CaretRight

  if (items.length === 0) return null

  const sales = sumBy(collections, "salesQuantity")
  const forecast = sumBy(collections, "forecastedUnits")
  const preOrders = sumBy(collections, "preOrderedUnits")

  return (
    <>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full px-4 py-2 bg-subtle border-b border-base text-left cursor-pointer"
      >
        <span className="label-base">Historical</span>
        <span className="paragraph-sm text-fg-subtle">({items.length})</span>
        <div className="ml-auto flex items-center gap-3 paragraph-sm text-fg-subtle">
          <span>
            {formatNumber(sales)} {formatName("sale", true)}
          </span>
          <span className="text-fg-muted">·</span>
          <span>{formatNumber(forecast)} forecast</span>
          <span className="text-fg-muted">·</span>
          <span>
            {formatNumber(preOrders)} {formatName("preorder", true)}
          </span>
        </div>
        <Caret size={14} className="text-fg-muted shrink-0" />
      </button>
      {expanded &&
        items.map((item) => (
          <FashionOverviewItem
            key={item.type === "group" ? `group-${item.groupId}` : item.collection.id}
            item={item}
            variant="historic"
            today={today}
          />
        ))}
    </>
  )
}

function RouteComponent() {
  const router = useRouter()
  const { data: collections, isLoading } = useGetCollectionsWithDetails()
  const { data: variantCategories } = api.productManagement.categorizeVariants.useQuery()
  usePageTracking("Collections - Overview")

  const [upcomingExpanded, setUpcomingExpanded] = useState(false)

  const today = startOfTodayUTC()
  const fashionCollections = (collections ?? []).filter((c) => c.collectionType !== "noos")
  const noosCollections = (collections ?? []).filter((c) => c.collectionType === "noos")

  const hasFashion = fashionCollections.length > 0
  const hasNoos = noosCollections.length > 0

  const { current, future, historic } = groupAndClassifyCollections(fashionCollections, today)
  const noosItems = groupCollectionsAsItems(noosCollections)

  const noosSales = sumBy(noosCollections, "salesQuantity")
  const noosForecasted = sumBy(noosCollections, "forecastedUnits")
  const noosPreOrders = sumBy(noosCollections, "preOrderedUnits")

  const visibleFuture = upcomingExpanded ? future : future.slice(0, 3)
  const hiddenFutureCount = future.length - 3

  const currentCollections = collectionsFromItems(current)
  const futureCollections = collectionsFromItems(future)
  const historicCollections = collectionsFromItems(historic)

  const hasCollections = (collections?.length ?? 0) > 0

  return (
    <Module
      breadcrumb={<BreadcrumbWrapper />}
      actions={
        <>
          <Button
            variant="neutral"
            size="sm"
            onClick={() => router.navigate({ to: "/collection-management" })}
          >
            Manage Collections
          </Button>
          {hasCollections && (
            <DownloadManagerDialog
              collections={collections ?? []}
              trigger={
                <IconButton variant="neutral" size="sm" aria-label="Export forecasts">
                  <DownloadSimple />
                </IconButton>
              }
            />
          )}
        </>
      }
    >
      <OverviewPage>
        {isLoading ? (
          <LoadingOverlay />
        ) : !hasCollections ? (
          <div className="flex-1 flex flex-col gap-2 items-center justify-center">
            <img src="/no_collections.png" alt="No collection" className="w-[220px] h-[160px]" />
            <p className="heading-sm">Collections</p>
            <p className="paragraph-base text-fg-subtle text-center mb-4 w-[220px]">
              No collections yet! Create your first collection to get started.
            </p>
            <ManageCollectionDialog />
          </div>
        ) : (
          <div className="flex flex-col gap-4 w-full">
            {(variantCategories?.inbox.length ?? 0) > 0 && (
              <Link
                to="/product-management/inbox"
                className="flex items-center gap-3 rounded-lg border border-base bg-field-component px-4 py-3 hover:border-strong transition-colors"
              >
                <Tray className="h-5 w-5 text-icon-yellow" />
                <div className="flex flex-col">
                  <span className="label-base">
                    {formatNumber(variantCategories?.inbox.length ?? 0, 0)} variant
                    {(variantCategories?.inbox.length ?? 0) !== 1 && "s"} to review
                  </span>
                  <span className="paragraph-sm text-fg-subtle">
                    Variants needing action: add to collection, hide, or phase out
                  </span>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 text-fg-subtle" />
              </Link>
            )}

            <div className="flex gap-6 items-start w-full">
              {/* Fashion Card */}
              <Card className="flex-1 min-w-0">
                <CardHeader>
                  <span className="label-base">Fashion</span>
                  {hasFashion && <ViewAllLink to="/demand-planning/fashion" />}
                </CardHeader>

                {!hasFashion ? (
                  <CardEmptyState>No fashion collections yet</CardEmptyState>
                ) : (
                  <>
                    <HistoricSection
                      items={historic}
                      collections={historicCollections}
                      today={today}
                    />

                    <SectionDivider
                      label="Current"
                      count={current.length}
                      collections={currentCollections}
                    />
                    {current.length === 0 ? (
                      <div className="px-4 py-3 paragraph-sm text-fg-subtle">
                        No current collections
                      </div>
                    ) : (
                      current.map((item) => (
                        <FashionOverviewItem
                          key={item.type === "group" ? `group-${item.groupId}` : item.collection.id}
                          item={item}
                          variant="current"
                          today={today}
                        />
                      ))
                    )}

                    <SectionDivider
                      label="Upcoming"
                      count={future.length}
                      collections={futureCollections}
                    />
                    {future.length === 0 ? (
                      <div className="px-4 py-3 paragraph-sm text-fg-subtle">
                        No upcoming collections
                      </div>
                    ) : (
                      <>
                        {visibleFuture.map((item) => (
                          <FashionOverviewItem
                            key={
                              item.type === "group" ? `group-${item.groupId}` : item.collection.id
                            }
                            item={item}
                            variant="future"
                            today={today}
                          />
                        ))}
                        {!upcomingExpanded && hiddenFutureCount > 0 && (
                          <button
                            onClick={() => setUpcomingExpanded(true)}
                            className="w-full px-4 py-2.5 paragraph-sm text-fg-subtle hover:bg-subtle transition-colors text-left"
                          >
                            Show {hiddenFutureCount} more
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </Card>

              {/* NOOS Card */}
              <Card className="flex-1 min-w-0">
                <CardHeader>
                  <span className="label-base">NOOS</span>
                  {hasNoos && <ViewAllLink to="/demand-planning/noos" />}
                </CardHeader>

                {!hasNoos ? (
                  <CardEmptyState>No NOOS collections yet</CardEmptyState>
                ) : (
                  <>
                    <div className="px-4 py-2 bg-subtle border-b border-base flex items-center gap-2 paragraph-sm text-fg-subtle">
                      <span>{noosCollections.length} collections</span>
                      <div className="ml-auto flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          {formatNumber(noosSales)} {formatName("sale", true)}
                          <Tooltip
                            title={`${formatName("sale", true, true)} data: ${formatDate(subWeeks(startOfTodayUTC(), 52), DateFormatType.FullDateShort)} – ${formatDate(startOfTodayUTC(), DateFormatType.FullDateShort)}`}
                            description={`NOOS collections are limited to the last 52 weeks of historical ${formatName("sale", true)} data`}
                          >
                            <Info size={12} className="text-fg-muted cursor-default" />
                          </Tooltip>
                        </span>
                        <span className="text-fg-muted">·</span>
                        <span>{formatNumber(noosForecasted)} forecast</span>
                        <span className="text-fg-muted">·</span>
                        <span>
                          {formatNumber(noosPreOrders)} {formatName("preorder", true)}
                        </span>
                      </div>
                    </div>

                    {noosItems.map((item) =>
                      item.type === "group" ? (
                        <NoosGroupOverviewRow
                          key={`group-${item.groupId}`}
                          groupId={item.groupId}
                          groupName={item.groupName}
                          collections={item.collections}
                        />
                      ) : (
                        <NoosOverviewRow key={item.collection.id} collection={item.collection} />
                      ),
                    )}
                  </>
                )}
              </Card>
            </div>
          </div>
        )}
      </OverviewPage>
    </Module>
  )
}
